import { Injectable } from '@angular/core';
import { ToolsService } from './tools.service';
import { Company, LoadOrganizationsResponse, Organization, UserDataForAdmin } from '../types';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable, of } from 'rxjs';
import { tap, shareReplay } from 'rxjs/operators';
import { EnabledService, Softwares } from '../enums';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { OrganizationsAndCompanies } from '../types/organization-companies.model';

@UntilDestroy()
@Injectable()
export class MainService {
  public rowsPerPageAdminSettings: number = JSON.parse(localStorage.getItem('rowsPerPageAdminSettings') || '100');
  public organizationsMap = new Map<string, Organization>();
  public dropdownOrganizationsAndCompanies: OrganizationsAndCompanies[] = [];

  private loadOrganizationsRequest$: Observable<LoadOrganizationsResponse> | null = null;

  chatChartData: any = [{ data: [], label: 'Chats' }];
  chatChartLabels: any = [];
  chatChartColors: any = [
    {
      backgroundColor: 'rgba(148,159,177,0.2)',
      borderColor: 'rgba(148,159,177,1)',
      pointBackgroundColor: 'rgba(148,159,177,1)',
      pointBorderColor: '#fff',
      pointHoverBackgroundColor: '#fff',
      pointHoverBorderColor: 'rgba(148,159,177,0.8)',
    },
  ];

  fallbackChartData: any = [{ data: [], label: 'Fallbacks' }];
  fallbackChartLabels: any = [];
  fallbackChartColors: any = [
    {
      backgroundColor: 'rgba(148,159,177,0.2)',
      borderColor: 'rgba(148,159,177,1)',
      pointBackgroundColor: 'rgba(148,159,177,1)',
      pointBorderColor: '#fff',
      pointHoverBackgroundColor: '#fff',
      pointHoverBorderColor: 'rgba(148,159,177,0.8)',
    },
  ];

  maintenanceChartData: any = [{ data: [0, 0, 0], label: 'Maintenance' }];
  maintenanceChartLabels: any = ['Professional', 'Business', 'Elite'];
  // maintenanceChartLabels: any = [];
  maintenanceChartColors: any = [
    {
      backgroundColor: 'rgba(148,159,177,0.2)',
      borderColor: 'rgba(148,159,177,1)',
      pointBackgroundColor: 'rgba(148,159,177,1)',
      pointBorderColor: '#fff',
      pointHoverBackgroundColor: '#fff',
      pointHoverBorderColor: 'rgba(148,159,177,0.8)',
    },
  ];

  refreshChatChart: any = true;
  refreshFallbacksChart: any = true;
  refreshMaintenanceChart: any = true;

  NS_URL: string = environment.NS_URL;

  userData: any = JSON.parse(localStorage.getItem('userData'));
  userID: string = localStorage.getItem('userID');
  firebaseUserID: string = localStorage.getItem('firebaseUserID');
  totalRequests = 0;

  requests: any = [];
  landlordsCount: any = 0;
  totalFallbacks24: any = 0;
  eliteUsersCount: any = 0;
  businessUsersCount: any = 0;
  totalNumberOfUnits: any = 0;
  lastMonthLandlords: any = 0;
  landlordPlans: any = ['FREE', 'PROFESSIONAL', 'BUSINESS', 'ELITE'];
  managementSoftware = Object.values(Softwares);
  services: EnabledService[] = Object.values(EnabledService);
  last24HoursLandlords: any = 0;
  setting: any = {
    organizationIDs: [],
    managementSoftware: this.managementSoftware,
    landlordPlans: this.landlordPlans,
    service: this.services,
  };
  subUsers: any = [];
  companyName: any;
  organizations: Organization[] = [];
  companies: Company[] = [];
  EMAIL_SERVICE_BASE_URL = environment.EMAIL_SERVICE_BASE_URL;

  constructor(
    public http: HttpClient,
    public tools: ToolsService
  ) {
    this.loadUserData();
    this.setDateForChart();
    this.setDateForChart2();
  }

  public updateUserData(): void {
    this.http
      .put(`${this.NS_URL}/users/admin`, this.userData)
      .pipe(untilDestroyed(this))
      .subscribe(() => this.loadUserData());
  }

  setDateForChart() {
    for (let i = 0; i < 90; i++) {
      this.fallbackChartData[0].data.push(0);
      const today = new Date();
      const priorDate = new Date().setDate(today.getDate() - i);
      this.fallbackChartLabels.unshift(
        this.tools.months[new Date(priorDate).getMonth()] + ' ' + new Date(priorDate).getDate()
      );
    }
  }
  setDateForChart2() {
    this.chatChartData[0].data = [];
    this.chatChartLabels = [];
    for (let i = 0; i < 30; i++) {
      this.chatChartData[0].data.push(0);
      const today = new Date();
      const priorDate = new Date().setDate(today.getDate() - i);
      this.chatChartLabels.unshift(new Date(priorDate).getDate());
    }
  }

  loadUserData() {
    this.http
      .get<{ data: UserDataForAdmin }>(`${this.tools.NS_URL}/users/get-admin`)
      .pipe(untilDestroyed(this))
      .subscribe((res) => {
        if (res.data) {
          this.userData = res.data;
          localStorage.setItem('lastLogin', Date.now().toString());
          localStorage.setItem('userData', JSON.stringify(this.userData));
          this.userData.fullName = this.userData.firstName + ' ' + this.userData.lastName;
          this.userData.landlordID = this.userData.landlordID || this.userData._id;
        }
      });
  }

  public loadOrganizations(force: boolean = false): Observable<LoadOrganizationsResponse> {
    // Return cached data if available and not forcing refresh
    if (this.organizations.length > 0 && !force) {
      return of({ data: this.organizations });
    }

    // Clear cache if forcing refresh
    if (force) {
      this.loadOrganizationsRequest$ = null;
    }

    // Return existing request or create new one
    if (!this.loadOrganizationsRequest$) {
      this.loadOrganizationsRequest$ = this.http
        .get<LoadOrganizationsResponse>(`${this.NS_URL}/admin/organizations`)
        .pipe(
          tap((response) => {
            this.organizations = response.data;
            this.createOrganizationsMap();
          }),
          shareReplay(1) // Cache the result and share among subscribers
        );
    }

    return this.loadOrganizationsRequest$;
  }

  private createOrganizationsMap(): void {
    this.organizationsMap.clear();
    this.organizations.forEach((org) => {
      if (org._id) {
        this.organizationsMap.set(org._id, {
          name: org.name,
          organizationID: org._id,
          companies: org.companies || [],
        });
      }
    });
    this.dropdownOrganizationsAndCompanies = this.flattenOrganizations(this.organizations);
  }

  private flattenOrganizations(organizations: Organization[]): OrganizationsAndCompanies[] {
    const flattenedArray: OrganizationsAndCompanies[] = [];

    organizations.forEach((org) => {
      // Add the organization part
      flattenedArray.push({
        name: org.name,
        organizationID: org._id || '',
        _id: `org_${org._id}`,
      });

      // Add each company part
      org.companies?.forEach((comp) => {
        flattenedArray.push({
          company_name: comp.company,
          organization_name: org.name,
          companyID: comp._id,
          organizationID: org._id || '',
          _id: `comp_${comp._id}`,
        });
      });
    });

    return flattenedArray;
  }

  DisableCompanyConversation(companyID: any) {
    const disabledCompanies = ['60192a66c7b4183e949ccc22'];
    return disabledCompanies.includes(companyID) ? true : false;
  }

  public updatePaginationNumber(): void {
    localStorage.setItem('rowsPerPageAdminSettings', this.rowsPerPageAdminSettings.toString());
  }
}
